import React from "react";
import { useTranslation } from "react-i18next";
import { Box, ButtonBase, Chip, Typography } from "@mui/material";
import { PaymentsOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { documentCraftingFileUrls } from "../../../Api/documentCrafting";
import {
  DocumentCraftingTypeDefinition,
  DocumentCraftingTypeKey,
} from "../../../Models/documentCrafting";
import { useDocumentCraftingTypes } from "../../../Query/useDocumentCrafting";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import AuthImage from "../Common/AuthImage";
import {
  DocumentTypeIcon,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../Common/DocumentCraftingUi";
import { formatCost } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";

interface Props {
  onSelect: (key: DocumentCraftingTypeKey) => void;
}

/** "1 page", "1–2 pages", "derived from the speaking time" (decks). */
export const usePagesText = () => {
  const { t } = useTranslation("documentCrafting");
  return (definition: DocumentCraftingTypeDefinition) => {
    const { min_pages: min, max_pages: max, engine } = definition;
    if (engine === "deck") return t("create.types.slidesFromDuration");
    if (min && max && min !== max) return t("create.types.pageRange", { min, max });
    const pages = max ?? min;
    return pages ? t("create.types.pages", { count: pages }) : "";
  };
};

/** "Uses AI; typical cost for this type: $0.42" (hidden below 5 runs). */
export const TypicalCost: React.FC<{ cost: number | null }> = ({ cost }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  if (cost === null || cost === undefined) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: color.gray500 }}>
      <PaymentsOutlined sx={{ fontSize: 16 }} />
      <Typography sx={{ fontSize: 12, color: color.gray500 }}>
        {t("create.types.typicalCost", { cost: formatCost(cost, i18n.language) })}
      </Typography>
    </Box>
  );
};

const TypeCard: React.FC<{
  definition: DocumentCraftingTypeDefinition;
  onSelect: () => void;
}> = ({ definition, onSelect }) => {
  const labels = useDocumentCraftingEnumLabels();
  const pagesText = usePagesText();
  const { accent, accentBorder } = useDashboardColors();
  const name = labels.type(definition.key);

  return (
    <ButtonBase
      onClick={onSelect}
      focusRipple
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        borderRadius: "14px",
        overflow: "hidden",
        border: `1px solid ${color.gray200}`,
        backgroundColor: color.white,
        transition: "border-color 150ms, box-shadow 150ms",
        "&:hover, &.Mui-focusVisible": {
          borderColor: accentBorder,
          boxShadow: "0 6px 18px rgba(17, 24, 39, 0.08)",
        },
      }}
    >
      <Box sx={{ height: 170, backgroundColor: color.gray100, p: 1.5 }}>
        <AuthImage
          url={
            definition.has_thumbnail
              ? documentCraftingFileUrls.typeThumbnail(definition.key)
              : null
          }
          alt={name}
          sx={{ height: "100%", backgroundColor: "transparent" }}
          fallback={
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DocumentTypeIcon
                typeKey={definition.key}
                size={44}
                iconColor={color.gray300}
                tooltip={false}
              />
            </Box>
          }
        />
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DocumentTypeIcon typeKey={definition.key} iconColor={accent} tooltip={false} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: color.gray900 }}>
            {name}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: color.gray600, flex: 1 }}>
          {labels.typeDescription(definition.key)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          {definition.outputs.map((output) => (
            <Chip
              key={output}
              size="small"
              label={output.toUpperCase()}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: color.gray100,
                color: color.gray700,
              }}
            />
          ))}
          <Typography sx={{ fontSize: 12, color: color.gray500 }}>
            {pagesText(definition)}
          </Typography>
        </Box>
        <TypicalCost cost={definition.typical_cost} />
      </Box>
    </ButtonBase>
  );
};

/** Step 1 of creating: one card per available document type. */
const TypePicker: React.FC<Props> = ({ onSelect }) => {
  const { t } = useTranslation("documentCrafting");
  const types = useDocumentCraftingTypes();

  if (types.isLoading) return <LoadingState />;
  if (types.isError || !types.data) {
    return <ErrorState onRetry={() => types.refetch()} />;
  }

  // Phase-3 types are listed by the backend but stay hidden until enabled.
  const enabled = types.data.types.filter((definition) => definition.enabled);
  if (!enabled.length) {
    return <EmptyState title={t("create.types.none")} />;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
          xl: "repeat(4, minmax(0, 1fr))",
        },
        gap: 2,
      }}
    >
      {enabled.map((definition) => (
        <TypeCard
          key={definition.key}
          definition={definition}
          onSelect={() => onSelect(definition.key)}
        />
      ))}
    </Box>
  );
};

export default TypePicker;
